import React from 'react';
import { Helmet } from 'react-helmet';
import BaseAudioTranscriber from './BaseAudioTranscriber';

const TranscribeMP3 = () => {
    const formats = [
        { name: 'WAV', slug: 'wav' },
        { name: 'M4A', slug: 'm4a' },
        { name: 'AAC', slug: 'aac' },
        { name: 'OGG', slug: 'ogg' },
        { name: 'FLAC', slug: 'flac' }
    ];

    // Schema.org structured data for better SEO
    const schemaData = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebApplication",
                "name": "MP3 to Text Converter - Free MP3 Transcription Tool",
                "url": "https://echonote.xyz/tools/transcribe-to-text/mp3",
                "description": "Free online MP3 to text converter. Transcribe MP3 files to text instantly with high accuracy. Convert MP3 audio to text in 50+ languages.",
                "applicationCategory": "AudioTranscriptionApplication",
                "operatingSystem": "Any",
                "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "USD"
                },
                "featureList": [
                    "Free MP3 to text conversion",
                    "Support for 50+ languages", 
                    "High accuracy transcription",
                    "5MB file size limit",
                    "Privacy-focused processing"
                ]
            },
            {
                "@type": "HowTo",
                "name": "How to Convert MP3 to Text",
                "description": "Step-by-step guide to transcribe MP3 files to text using our free online tool",
                "step": [
                    {
                        "@type": "HowToStep",
                        "name": "Upload MP3 File",
                        "text": "Click or drag and drop your MP3 file (up to 5MB) into the upload area"
                    },
                    {
                        "@type": "HowToStep", 
                        "name": "Start Transcription",
                        "text": "Click the 'Transcribe File' button to begin converting your MP3 to text"
                    },
                    {
                        "@type": "HowToStep",
                        "name": "Download Results",
                        "text": "Copy or download your transcribed text in minutes"
                    }
                ]
            },
            {
                "@type": "FAQPage",
                "mainEntity": [
                    {
                        "@type": "Question",
                        "name": "Is the MP3 to text converter free?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Yes, our MP3 to text transcription tool is completely free to use with no registration required."
                        }
                    },
                    {
                        "@type": "Question",
                        "name": "What is the maximum MP3 file size?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "You can upload MP3 files up to 5MB in size for transcription."
                        }
                    },
                    {
                        "@type": "Question",
                        "name": "How accurate is the MP3 transcription?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Our MP3 to text converter provides high accuracy transcription with support for 50+ languages and clear audio recognition."
                        }
                    }
                ]
            }
        ]
    };

    return (
        <>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(schemaData)}
                </script>
            </Helmet>
            
            <BaseAudioTranscriber 
                title="Free MP3 to Text Converter | Transcribe MP3 Audio Files Online"
                description="Convert MP3 to text instantly with our free online MP3 transcription tool. Transcribe MP3 files and audio recordings to text with high accuracy in 50+ languages. No registration required."
                manualDescription="Upload your MP3 file (up to 5MB) and get accurate text transcription in minutes - completely free!"
                format="mp3"
                metaDescription="Free MP3 to text converter tool. Transcribe MP3 files to text online instantly. Convert MP3 audio recordings to text with high accuracy. Support for 50+ languages, no registration needed."
                metaKeywords="mp3 to text, transcribe mp3, mp3 transcription, convert mp3 to text, mp3 to text converter, transcribe mp3 to text, mp3 audio to text, mp3 file transcription, free mp3 transcriber, mp3 speech to text, mp3 to text free, transcribe mp3 file, mp3 transcript, convert mp3 audio to text, mp3 to text online"
                canonicalUrl="https://echonote.xyz/tools/transcribe-to-text/mp3"
                relatedFormats={formats}
                // Additional content for better SEO targeting high-volume keywords
                additionalContent={{
                    beforeFeatures: (
                        <div className="seo-content">
                            <div className="content-section">
                                <h2>Best Free MP3 to Text Converter Online</h2>
                                <p>
                                    Transform your MP3 audio files into accurate text transcripts with our advanced MP3 to text converter. 
                                    Whether you need to transcribe MP3 recordings, convert MP3 lectures to text, or extract text from MP3 files, 
                                    our free online tool delivers professional-quality results in minutes.
                                </p>
                            </div>
                            
                            <div className="content-section">
                                <h3>Why Choose Our MP3 Transcription Tool?</h3>
                                <ul>
                                    <li><strong>100% Free MP3 to Text Conversion</strong> - No hidden fees or subscription required</li>
                                    <li><strong>High Accuracy MP3 Transcription</strong> - Advanced AI ensures precise text output</li>
                                    <li><strong>50+ Language Support</strong> - Transcribe MP3 files in multiple languages</li>
                                    <li><strong>Fast Processing</strong> - Get your MP3 transcript in under 5 minutes</li>
                                    <li><strong>Privacy Protected</strong> - Your MP3 files are automatically deleted after transcription</li>
                                    <li><strong>No Software Download</strong> - Works directly in your browser</li>
                                </ul>
                            </div>

                            <div className="content-section">
                                <h3>Perfect for All MP3 Transcription Needs</h3>
                                <p>
                                    Our MP3 to text tool is ideal for students transcribing lectures, journalists converting interviews, 
                                    content creators extracting text from podcasts, researchers analyzing audio data, and professionals 
                                    who need to convert MP3 recordings to searchable text documents.
                                </p>
                            </div>
                        </div>
                    ),
                    afterFeatures: (
                        <div className="seo-content">
                            <div className="content-section">
                                <h3>How Our MP3 to Text Converter Works</h3>
                                <ol>
                                    <li><strong>Upload Your MP3 File</strong> - Drag and drop or click to select your MP3 audio file (up to 5MB)</li>
                                    <li><strong>Automatic Processing</strong> - Our AI analyzes your MP3 and converts speech to text</li>
                                    <li><strong>Download Results</strong> - Copy your transcribed text or download the transcript file</li>
                                </ol>
                            </div>

                            <div className="content-section">
                                <h3>Supported MP3 Transcription Features</h3>
                                <div className="features-list">
                                    <div className="feature-item">
                                        <strong>Multiple Audio Formats:</strong> While specialized for MP3, also supports WAV, M4A, AAC, OGG, and FLAC
                                    </div>
                                    <div className="feature-item">
                                        <strong>Speaker Recognition:</strong> Identifies different speakers in MP3 conversations
                                    </div>
                                    <div className="feature-item">
                                        <strong>Timestamp Support:</strong> Optional timestamps for MP3 transcripts
                                    </div>
                                    <div className="feature-item">
                                        <strong>Batch Processing:</strong> Transcribe multiple MP3 files efficiently
                                    </div>
                                </div>
                            </div>

                            <div className="content-section">
                                <h3>Frequently Asked Questions</h3>
                                <div className="faq-item">
                                    <h4>Can I transcribe long MP3 files?</h4>
                                    <p>Currently supports MP3 files up to 5MB and 5 minutes in length for optimal accuracy and processing speed.</p>
                                </div>
                                <div className="faq-item">
                                    <h4>Is my MP3 file data secure?</h4>
                                    <p>Absolutely. We prioritize privacy - your MP3 files are processed securely and automatically deleted after transcription.</p>
                                </div>
                                <div className="faq-item">
                                    <h4>What languages can transcribe MP3 to text?</h4>
                                    <p>Our MP3 transcription tool supports over 50 languages including English, Spanish, French, German, Chinese, Japanese, and many more.</p>
                                </div>
                            </div>
                        </div>
                    )
                }}
            />
        </>
    );
};

export default TranscribeMP3; 